import * as mutations from "../graphql/mutations";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinearProgressWithLabel from "./LinearProgressWIthLabel";
import DisplayGeneratedText from "./DisplayGeneratedText";
import { apiClient } from "../helpers/apiClient";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CGI_BLACK,
  CGI_GREY,
  CGI_PURPLE,
  CGI_RED,
  CGI_WARNING,
  discreteDescriptions,
  returnColor,
} from "../helpers/enums";

export default function DisplayModuleData({
  evalModuleData,
  fetchEvaluation,
  setOpenLoading,
  setNotificationMessage,
  setOpenNotification,
  reloadPdf,
  index,
}) {
  const { t } = useTranslation();
  const questionRatings = evalModuleData.questionRating || [];

  async function updateEvaluationModuleData(newText) {
    setOpenLoading(true);
    try {
      await apiClient.graphql({
        query: mutations.updateEvaluationModuleData,
        variables: {
          input: {
            id: evalModuleData.id,
            resultText: newText,
          },
        },
      });
      await fetchEvaluation();
      reloadPdf();
      setNotificationMessage(t("textSaveSuccessful"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenLoading(false);
    setOpenNotification(true);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mb: 4,
        pb: 4,
        borderBottom: 1,
        borderColor: CGI_BLACK,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          width: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: 19, fontWeight: 500, mr: 1 }}>
            {index + 1}.
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {evalModuleData.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifySelf: "center",
            mx: 1,
          }}
        >
          {evalModuleData?.completelyAnswered ? (
            <LinearProgressWithLabel
              progressValue={
                (evalModuleData.rating / evalModuleData.maxRating) * 100
              }
              header={true}
            />
          ) : (
            <Typography sx={{ mb: 0.2, fontSize: 15, color: CGI_WARNING }}>
              {evalModuleData.name}: {t("notCompletelyAnswered")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          mx: 2.5,
          mt: 1,
          mb: 3,
          pt: 0.5,
          pb: 2,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "#bbb",
        }}
      >
        <DisplayGeneratedText
          text={evalModuleData.resultText}
          saveAndRefetchText={updateEvaluationModuleData}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 450,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            {questionRatings.length <= 2 ? (
              <BarChart
                barSize={100}
                data={questionRatings
                  ?.filter((x) => x.hasAnswer)
                  .map((x, i) => ({
                    ...x,
                    radarName: `${t("question")} ${index + 1}.${i + 1}`,
                    ratingInPercent:
                      x.maxRating > 0 ? x.rating / x.maxRating : 0,
                  }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="radarName" />
                <YAxis domain={[0, 1]} />
                <Tooltip />
                <Legend />
                <Bar
                  name={`${t("questionFulfillment")} `}
                  dataKey="ratingInPercent"
                  stroke={CGI_RED}
                  fill={CGI_RED}
                  activeBar={
                    <Rectangle fill={CGI_PURPLE} stroke={CGI_PURPLE} />
                  }
                />
              </BarChart>
            ) : (
              <RadarChart
                id={"radarChartId" + index}
                cx="50%"
                cy="50%"
                outerRadius="90%"
                data={questionRatings
                  ?.filter((x) => x.hasAnswer)
                  .map((x, i) => ({
                    ...x,
                    radarName: t("question") + (index + 1) + "." + (i + 1),
                    ratingInPercent: x.rating / 100,
                  }))}
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="radarName" />
                <PolarRadiusAxis domain={[0, 1]} />
                <Radar
                  name="Data"
                  dataKey="ratingInPercent"
                  stroke={CGI_RED}
                  fill={CGI_RED}
                  fillOpacity={0.6}
                />
              </RadarChart>
            )}
          </ResponsiveContainer>
        </Box>
      </Box>

      {questionRatings
        ?.filter((x) => x.hasAnswer)
        .map((questionRating, i) => (
          <Accordion
            key={i}
            sx={{
              border: 1,
              borderRadius: "20px !important",
              borderColor: CGI_GREY,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: 1,
                }}
              >
                <Typography
                  sx={{
                    minWidth: 100,
                    fontWeight: 500,
                  }}
                >
                  {t("question")} {index + 1}.{i + 1}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: 13.5,
                  }}
                >
                  {questionRating.questionData?.text || ""}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifySelf: "center",
                  ml: 1,
                  alignItems: "center",
                }}
              >
                <LinearProgressWithLabel
                  progressValue={
                    (questionRating.rating / questionRating.maxRating) * 100
                  }
                  width={120}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ borderTop: 1, borderColor: "#bbb" }}>
              {(questionRating.requirements || []).map((req) =>
                (req.requirementAnswers || []).map((element) => {
                  const isGradation = req.gradations;
                  const maxLevel = req.recommendations?.length
                    ? req.recommendations.length - 1
                    : 0;
                  const selected = element.selectedLevel ?? 0;

                  let displayText = "";
                  let showRecommendation = false;

                  if (element.irrelevant) {
                    displayText = t("notRelevant");
                  } else if (isGradation) {
                    const discreteIndex =
                      maxLevel > 0 ? Math.round((selected / maxLevel) * 3) : 0;
                    displayText = t(discreteDescriptions[discreteIndex]);
                    showRecommendation = selected < maxLevel;
                  } else {
                    if (element.fulfilled === true) {
                      displayText = t("fulfilled");
                      showRecommendation = false;
                    } else if (element.fulfilled === false) {
                      displayText = t("notFulfilled");
                      showRecommendation = true;
                    } else {
                      displayText = "";
                      showRecommendation = false;
                    }
                  }

                  return (
                    <Box key={element.id}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          ml: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          align="right"
                          sx={{
                            mr: 1,
                            fontWeight: 500,
                            minWidth: 150,
                            maxWidth: 150,
                            color: returnColor(
                              element.irrelevant,
                              selected,
                              maxLevel,
                              element.fulfilled,
                            ),
                          }}
                        >
                          {"• "}
                          {displayText}
                          {displayText ? ":" : ""}
                        </Typography>

                        <Typography sx={{ fontFamily: "Inter", fontSize: 13 }}>
                          {req.text +
                            ` (${t("weights", { weight: req.weight })})`}
                        </Typography>
                      </Box>

                      {showRecommendation &&
                        req.recommendations?.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              mb: 0.5,
                              ml: 15,
                            }}
                          >
                            <Typography sx={{ fontWeight: 500, minWidth: 140 }}>
                              {"→ " + t("recommendation") + ": "}
                              {req.recommendations[selected]
                                ?.recommendationText ||
                                req.recommendations[0]?.recommendationText ||
                                t("noRecommendation")}
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  );
                }),
              )}
            </AccordionDetails>
          </Accordion>
        ))}
    </Box>
  );
}
